import { createContext } from 'react';
import { AbilityBuilder, PureAbility } from '@casl/ability';
import { createContextualCan } from '@casl/react';

export type Actions =
  | 'read'
  | 'manage'
  | 'create'
  | 'update'
  | 'delete'
  | 'assign';

export type Subjects =
  | 'Home'
  | 'Sessions'
  | 'Users'
  | 'Profile'
  | 'Organizations'
  | 'Flows'
  | 'IdUpload'
  | 'Developers'
  | 'DevHubApiKeys'
  | 'DevHubWebhooks'
  | 'DevHubSessionExpiry'
  | 'DevHubDocumentation'
  | 'DevHubStatus'
  | 'all';

export type AppAbility = PureAbility<[Actions, Subjects]>;

export const AbilityContext = createContext<AppAbility>(
  new PureAbility<[Actions, Subjects]>() as AppAbility,
);

export const Can = createContextualCan(AbilityContext.Consumer);

export const DEV_HUB_PERMISSIONS = {
  API_KEYS: 'read:devhub:apikeys',
  WEBHOOKS: 'read:devhub:Webhooks',
  SESSION_EXPIRY_READ: 'read:devhub:sessionexpiry',
  SESSION_EXPIRY_EDIT: 'edit:devhub:sessionexpiry',
  DOCUMENTATION: 'read:devhub:documentation',
  STATUS: 'read:devhub:status',
} as const;

export const DEV_HUB_READ_SUBJECTS: Subjects[] = [
  'DevHubApiKeys',
  'DevHubWebhooks',
  'DevHubSessionExpiry',
  'DevHubDocumentation',
  'DevHubStatus',
];

export function canAccessDevHub(ability: AppAbility): boolean {
  if (ability.can('manage', 'all') || ability.can('read', 'Developers')) {
    return true;
  }
  return DEV_HUB_READ_SUBJECTS.some(subject => ability.can('read', subject));
}

// ─── Permission string → CASL [action, subject] mapping ─────────────────────
// Matches the permission strings returned by the /roles API.

const PERMISSION_MAP: Array<{
  pattern: string | RegExp;
  action: Actions;
  subject: Subjects;
}> = [
  // Sessions
  { pattern: /^read:sessions:/, action: 'read', subject: 'Sessions' },
  { pattern: /^assign:sessions:/, action: 'assign', subject: 'Sessions' },
  // Creating sessions from Flow Management (also grants Flow Management read)
  { pattern: /^create:sessions:/, action: 'create', subject: 'Sessions' },
  { pattern: /^create:sessions:/, action: 'read', subject: 'Flows' },

  // Users (both user-management paths and record-level CRUD)
  { pattern: 'read:users:list', action: 'read', subject: 'Users' },
  { pattern: 'read:users:team-management', action: 'read', subject: 'Users' },
  { pattern: 'read:users:profile', action: 'read', subject: 'Profile' },
  { pattern: 'read:record:users', action: 'read', subject: 'Users' },
  { pattern: 'create:record:users', action: 'create', subject: 'Users' },
  { pattern: 'update:record:users', action: 'update', subject: 'Users' },
  { pattern: 'delete:record:users', action: 'delete', subject: 'Users' },

  // Organizations
  {
    pattern: 'read:record:organizations',
    action: 'read',
    subject: 'Organizations',
  },
  {
    pattern: 'create:record:organizations',
    action: 'create',
    subject: 'Organizations',
  },
  {
    pattern: 'update:record:organizations',
    action: 'update',
    subject: 'Organizations',
  },
  {
    pattern: 'delete:record:organizations',
    action: 'delete',
    subject: 'Organizations',
  },

  // Flows (Flow Management page, rules, design, etc.)
  { pattern: /^read:flows:/, action: 'read', subject: 'Flows' },
  { pattern: /^manage:flows:/, action: 'manage', subject: 'Flows' },
  // Creating new flows from the CMS Flow Management page.
  { pattern: 'create:flows:management', action: 'create', subject: 'Flows' },

  // ID Upload page access — any idupload permission grants both read + create
  // so the route guard (read IdUpload) always passes for these users.
  { pattern: 'create:flow:idupload', action: 'create', subject: 'IdUpload' },
  { pattern: 'create:flow:idupload', action: 'read', subject: 'IdUpload' },
  { pattern: 'read:flow:idupload', action: 'read', subject: 'IdUpload' },
  { pattern: 'read:flow:idupload', action: 'create', subject: 'IdUpload' },

  // Legacy Dev Hub umbrella permissions
  { pattern: /^read:developers:/i, action: 'read', subject: 'Developers' },
  { pattern: /^update:developers:/i, action: 'update', subject: 'Developers' },
  { pattern: /^manage:developers:/i, action: 'manage', subject: 'Developers' },
  { pattern: /^update:developers:/i, action: 'update', subject: 'DevHubWebhooks' },
  {
    pattern: /^update:developers:/i,
    action: 'update',
    subject: 'DevHubSessionExpiry',
  },

  // Granular Dev Hub permissions (IDs as created in Auth0 / permissions API)
  {
    pattern: /^read:devhub:apikeys$/i,
    action: 'read',
    subject: 'DevHubApiKeys',
  },
  {
    pattern: /^read:devhub:webhooks$/i,
    action: 'read',
    subject: 'DevHubWebhooks',
  },
  {
    pattern: /^read:devhub:sessionexpiry$/i,
    action: 'read',
    subject: 'DevHubSessionExpiry',
  },
  {
    pattern: /^edit:devhub:sessionexpiry$/i,
    action: 'update',
    subject: 'DevHubSessionExpiry',
  },
  {
    pattern: /^edit:devhub:sessionexpiry$/i,
    action: 'read',
    subject: 'DevHubSessionExpiry',
  },
  {
    pattern: /^read:devhub:documentation$/i,
    action: 'read',
    subject: 'DevHubDocumentation',
  },
  {
    pattern: /^read:devhub:status$/i,
    action: 'read',
    subject: 'DevHubStatus',
  },
];

const DEV_HUB_ALL_PERMISSIONS = [
  DEV_HUB_PERMISSIONS.API_KEYS,
  DEV_HUB_PERMISSIONS.WEBHOOKS,
  DEV_HUB_PERMISSIONS.SESSION_EXPIRY_READ,
  DEV_HUB_PERMISSIONS.SESSION_EXPIRY_EDIT,
  DEV_HUB_PERMISSIONS.DOCUMENTATION,
  DEV_HUB_PERMISSIONS.STATUS,
];

// ─── Fallback: role name → permission strings ────────────────────────────────
// Used when the JWT does not include a `permissions` claim directly.
// Mirrors the role definitions from /api/v1/roles.

const ROLE_PERMISSIONS: Record<string, string[]> = {
  superadmin: ['manage:all'],
  admin: [
    'read:sessions:details',
    'read:sessions:overview',
    'read:sessions:statistics',
    'read:users:list',
    'read:users:team-management',
    'read:users:profile',
    ...DEV_HUB_ALL_PERMISSIONS,
  ],
  // Auth0 role name is "Agent" (capital A)
  agent: [
    'delete:record:users',
    'read:sessions:details',
    'read:users:profile',
  ],
  'sonio-demo': [
    'read:sessions:details',
    'read:sessions:overview',
    'read:sessions:statistics',
    'read:users:profile',
  ],
  staff: [
    'assign:sessions:overview',
    'read:sessions:details',
    'read:sessions:overview',
    'read:sessions:statistics',
    'read:users:profile',
  ],
};

/**
 * Derive permission strings from role names using the local ROLE_PERMISSIONS
 * map. Useful when the JWT only contains role IDs instead of role names.
 */
export const derivePermissionsFromRoles = (roleNames: string[]): string[] => {
  const normalized = roleNames.map(r => r.toLowerCase());
  return [...new Set(normalized.flatMap(role => ROLE_PERMISSIONS[role] ?? []))];
};

// ─── Internal builder ────────────────────────────────────────────────────────

function applyPermissions(
  can: (action: Actions, subject: Subjects) => void,
  permissions: string[],
): void {
  if (permissions.includes('manage:all')) {
    can('manage', 'all');
    return;
  }

  let hasAny = false;

  for (const perm of permissions) {
    for (const { pattern, action, subject } of PERMISSION_MAP) {
      const matches =
        typeof pattern === 'string' ? perm === pattern : pattern.test(perm);
      if (matches) {
        can(action, subject);
        hasAny = true;
      }
    }
  }

  // Every authenticated user with at least one permission can see Home
  if (hasAny) {
    can('read', 'Home');
  }
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Build a CASL ability.
 *
 * @param roles       Role names from the JWT (e.g. ["admin", "staff"]).
 * @param permissions Permission strings from the JWT `permissions` claim.
 *                    When provided, role-based fallback is skipped.
 */
export const createAbility = (
  roles: string[],
  permissions?: string[],
): AppAbility => {
  const { can, build } = new AbilityBuilder<AppAbility>(PureAbility);

  if (permissions && permissions.length > 0) {
    applyPermissions(can, permissions);
  } else if (roles.length > 0) {
    const normalizedRoles = roles.map(r => r.toLowerCase());
    const derived = normalizedRoles.flatMap(
      role => ROLE_PERMISSIONS[role] ?? [],
    );
    applyPermissions(can, [...new Set(derived)]);
  }

  return build();
};
